import {
  Bell,
  CircleHelp,
  CreditCard,
  Info,
  LogOut,
  Monitor,
  Sparkles,
  TicketCheck,
  UserRound,
  UserRoundPlus,
} from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { type OrgExamType } from "@petergok/blue-collar-shared";
import LanguageSelector from "@/components/LanguageSelector";
import ShareEmployerLink from "./ShareEmployerLink";
import { ProfileSettingsList, ProfileSettingsRow } from "./ProfileSettingsList";
import AdminMode from "./AdminMode";

interface DesktopProfilePageProps {
  fullName: string;
  email: string;
  provider: string | null;
  accessLabel: string;
  accessDescription: string;
  isFree: boolean;
  isProfileComplete: boolean;
  showManagePayments: boolean;
  showReferral: boolean;
  isAdmin: boolean;
  orgExamType?: OrgExamType;
  onEditProfile: () => void;
  onNotificationSettings: () => void;
  onManagePayments: () => void;
  onRedeemCode: () => void;
  onReferral: () => void;
  onUpgrade: () => void;
  onFaqs: () => void;
  onNeedHelp: () => void;
  onSignOut: () => void;
}

function getInitials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

function getProviderLabel(provider: string | null, emailLabel: string) {
  if (!provider) return emailLabel;
  const normalized = provider.replace(/^oauth_/, "").replace(/_/g, " ");
  return normalized.replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function getDeviceLabel(
  browserFallback: string,
  platformFallback: string,
  formatLabel: (browser: string, platform: string) => string,
) {
  if (typeof navigator === "undefined") return browserFallback;

  const agent = navigator.userAgent;
  const browser = agent.includes("Edg/")
    ? "Edge"
    : agent.includes("Chrome/")
      ? "Chrome"
      : agent.includes("Firefox/")
        ? "Firefox"
        : agent.includes("Safari/")
          ? "Safari"
          : browserFallback;
  const platform = agent.includes("Mac OS X")
    ? "macOS"
    : agent.includes("Windows")
      ? "Windows"
      : agent.includes("Linux")
        ? "Linux"
        : platformFallback;

  return formatLabel(browser, platform);
}

export default function DesktopProfilePage({
  fullName,
  email,
  provider,
  accessLabel,
  accessDescription,
  isFree,
  isProfileComplete,
  showManagePayments,
  showReferral,
  isAdmin,
  orgExamType,
  onEditProfile,
  onNotificationSettings,
  onManagePayments,
  onRedeemCode,
  onReferral,
  onUpgrade,
  onFaqs,
  onNeedHelp,
  onSignOut,
}: DesktopProfilePageProps) {
  const { t } = useTranslation();
  const initials = useMemo(() => getInitials(fullName) || "S", [fullName]);
  const providerLabel = useMemo(
    () => getProviderLabel(provider, t("profile.email_provider")),
    [provider, t],
  );
  const deviceLabel = useMemo(
    () => getDeviceLabel(
      t("profile.web_browser"),
      t("profile.web_platform"),
      (browser, platform) => t("profile.device_label", { browser, platform }),
    ),
    [t],
  );

  return (
    <div className="min-h-full bg-[#f4f4f6]">
      <div className="border-b border-[#dddfe4] bg-white">
        <div className="dp-page-container flex min-h-[80px] items-center px-6 py-4">
          <div>
            <h1 className="text-lg font-medium leading-[27px] text-[#0a0c11]">{t("profile.account_title")}</h1>
            <p className="text-sm leading-[21px] text-[#5b616d]">{t("profile.account_subtitle")}</p>
          </div>
        </div>
      </div>

      <div className="dp-page-container grid items-start gap-6 p-6 xl:grid-cols-[minmax(0,1fr)_360px]">
        <div className="flex min-w-0 flex-col gap-5">
          <section className="flex min-h-[129px] items-center justify-between gap-6 rounded-[28px] border border-[#dddfe4] bg-white p-6">
            <div className="flex min-w-0 items-center gap-4">
              <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-[var(--dp-brand-deep)] text-lg font-bold text-white">
                {initials}
              </span>
              <div className="min-w-0">
                <h2 className="truncate text-xl font-bold leading-7 text-[#0a0c11]">{fullName}</h2>
                <p className="truncate text-sm leading-[21px] text-[#5b616d]">{email}</p>
                <span className="mt-1 inline-flex rounded-full bg-[#f1f3f5] px-2 py-[3px] text-xs font-medium leading-[18px] text-[#5b616d]">
                  {t("profile.managed_by_source", { provider: providerLabel })}
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={onEditProfile}
              className="dp-button-secondary h-11 shrink-0 px-5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--dp-brand-button)] focus-visible:ring-offset-2"
            >
              {t("profile.edit_profile")}
            </button>
          </section>

          <ProfileSettingsList label={t("profile.section_account_title")}>
            <ProfileSettingsRow
              icon={UserRound}
              label={isProfileComplete ? t("profile.edit_profile_info") : t("profile.create_profile")}
              onClick={onEditProfile}
            />
            <LanguageSelector variant="settings" />
            <ProfileSettingsRow
              icon={Bell}
              label={t("profile.notification_settings")}
              onClick={onNotificationSettings}
            />
            {showReferral && (
              <ProfileSettingsRow
                icon={UserRoundPlus}
                label={t("referral.invite_card_title")}
                onClick={onReferral}
              />
            )}
            {showManagePayments && (
              <ProfileSettingsRow
                icon={CreditCard}
                label={t("profile.manage_payments")}
                onClick={onManagePayments}
              />
            )}
            {orgExamType && (
              <ProfileSettingsRow
                icon={TicketCheck}
                label={t("profile.enter_access_code")}
                onClick={onRedeemCode}
              />
            )}
            {orgExamType && <ShareEmployerLink examType={orgExamType} variant="settings" />}
          </ProfileSettingsList>

          <ProfileSettingsList label={t("profile.section_help_title")}>
            <ProfileSettingsRow icon={Info} label={t("profile.faqs")} onClick={onFaqs} />
            <ProfileSettingsRow icon={CircleHelp} label={t("profile.need_help")} onClick={onNeedHelp} />
          </ProfileSettingsList>

          {isAdmin && (
            <ProfileSettingsList label={t("profile.admin_tools")}>
              <AdminMode variant="settings" />
            </ProfileSettingsList>
          )}
        </div>

        <aside className="flex flex-col gap-5">
          <section className="flex min-h-[129px] flex-col gap-3 rounded-[28px] border border-[#dddfe4] bg-white p-6">
            <div className="flex items-center gap-3">
              <span className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-xl bg-[var(--dp-brand-soft)] text-[var(--dp-brand)]">
                <Sparkles aria-hidden="true" className="h-6 w-6" />
              </span>
              <div>
                <h2 className="text-base font-semibold leading-6 text-[#0a0c11]">{isFree ? t("profile.free_plan") : accessLabel}</h2>
                <p className="text-sm leading-[21px] text-[#5b616d]">{isFree ? t("profile.limited_sessions") : accessDescription}</p>
              </div>
            </div>
            {(isFree || showManagePayments) && (
              <button
                type="button"
                onClick={isFree ? onUpgrade : onManagePayments}
                className="dp-button-primary h-11 w-full px-5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--dp-brand-button)] focus-visible:ring-offset-2"
              >
                {isFree ? t("profile.upgrade_to_pro") : t("profile.manage_subscription")}
              </button>
            )}
          </section>

          <section className="flex min-h-[181px] flex-col gap-3 rounded-[28px] border border-[#dddfe4] bg-white p-6">
            <h2 className="text-base font-semibold leading-6 text-[#0a0c11]">{t("profile.this_device")}</h2>
            <div className="flex items-center gap-3">
              <Monitor aria-hidden="true" className="h-6 w-6 shrink-0 text-[#5b616d]" />
              <div>
                <p className="text-sm font-semibold leading-[21px] text-[#0a0c11]">{deviceLabel}</p>
                <p className="text-sm leading-[21px] text-[#5b616d]">{t("profile.last_active_now")}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={onSignOut}
              className="flex h-11 w-full items-center justify-center gap-2 rounded-full border-[1.5px] border-[#c20000] px-5 text-sm font-semibold text-[#c20000] transition-colors hover:bg-[#fff1f1] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c20000] focus-visible:ring-offset-2"
            >
              <LogOut aria-hidden="true" className="h-5 w-5" />
              {t("auth.sign_out")}
            </button>
          </section>
        </aside>
      </div>
    </div>
  );
}
